import React, { useCallback } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

export type RefetchAudioButtonProps = {
	buttonText: MessageDescriptor;
};

const styles = cssMap({
	buttonWrapper: {
		marginTop: token('space.100'),
	},
});

export const RefetchAudioButton = ({ buttonText }: RefetchAudioButtonProps) => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { state: streamingState } = useAudioPlayerStreamingStore();

	const isLoading = streamingState.state === AudioStreamingState.LOADING;

	const { formatMessage } = useIntl();

	const startNewAudio = useCallback(() => {
		fetchAudio();
	}, [fetchAudio]);

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	const handleClick = useCallback(() => {
		stopExistingAudio();
		startNewAudio();
	}, [startNewAudio, stopExistingAudio]);

	return (
		<Box xcss={styles.buttonWrapper}>
			<Button
				testId="refetch-audio-button"
				shouldFitContainer
				appearance="primary"
				onClick={handleClick}
				isLoading={isLoading}
			>
				{formatMessage(buttonText)}
			</Button>
		</Box>
	);
};
